import {
  MODEL_ANIMATIONS,
  distanceRule,
  keepsDistance,
  modelFootprint,
  modelStanding,
  worldIn,
  worldStateAt,
  type CatalogueWorld,
  type Lesson,
  type ModelAnimation,
  type ModelFacts,
  type Step,
} from '@vrlearning/lesson-schema';
import { catalogueOfServed } from '../api/served-catalogue.js';
import { LOOK_STEP } from '../engine/test-panel.js';
import type { QuestPreviewAnswer } from './fetch.js';
import { objectPoints, worldPoints, type TeleportPoint } from './points.js';
import { BEKIJKEN_TEXT, clipLabel } from './text.js';

/**
 * Bekijken mode's lesson (ADR-058): built here from what the ticket gave, never stored, played with the
 * ordinary player under a bare lectern. A world: one step in it, every feature in its first state (a
 * placement-only world: its furnish step, as the test drive shows it). An object: the model in the
 * studio at its real size, facing the pupil, 2.5 m away or as much further as the distance rule asks
 * (the same functions the validator uses, so the lesson passes), a step to look and one per clip.
 */
export type BekijkenPlan = {
  readonly lesson: Lesson;
  readonly world: {
    readonly slug: string;
    readonly placement: boolean;
    readonly features: CatalogueWorld['features'];
    /** Each feature's state at the start, as the player sets it. */
    readonly initial: ReadonlyMap<string, string>;
  } | null;
  readonly object: {
    readonly slug: string;
    readonly clips: readonly ModelAnimation[];
    readonly pos: [number, number, number];
    readonly turn: number;
    readonly reach: number;
  } | null;
  readonly points: readonly TeleportPoint[];
};

/** Where the item stands at the nearest: 2.5 m in front of the pupil (spec). */
export const PREVIEW_DISTANCE = 2.5;
const STEP = 0.25;
/** Facing the pupil, who looks along −z from the origin. */
const FACING = 180;

function frame(kind: 'world' | 'object', name: string): Omit<Lesson, 'scene' | 'steps'> {
  return {
    schemaVersion: 1,
    id: `bekijken-${kind}`,
    title: `${BEKIJKEN_TEXT.look}: ${name}`.slice(0, 120),
    language: 'nl',
    mode: 'vr',
    audience: { ageMin: 10 },
    learningGoals: ['Een wereld of object op de Quest bekijken.'],
  };
}

/** Straight ahead, facing the pupil, from 2.5 m outwards until the distance rule is kept. */
export function standingPlace(facts: Pick<ModelFacts, 'box' | 'animations'>): {
  pos: [number, number, number];
  turn: number;
  reach: number;
} {
  const clips = Object.keys(facts.animations) as ModelAnimation[];
  const rule = distanceRule(modelStanding(facts));
  for (let distance = PREVIEW_DISTANCE; distance <= 200; distance += STEP) {
    const footprint = modelFootprint(facts, [0, -distance], FACING, { clips });
    if (keepsDistance(footprint, [0, 0], rule).ok) {
      const reach = Math.max(...footprint.map(([x, z]) => Math.hypot(x, z + distance)));
      return { pos: [0, 0, -distance], turn: FACING, reach: Number(reach.toFixed(2)) };
    }
  }
  throw new Error('no place keeps the distance rule');
}

export function bekijkenPlan(answer: QuestPreviewAnswer): BekijkenPlan | undefined {
  const name = answer.item.name;
  if (answer.item.kind === 'wereld') {
    const slug = Object.keys(answer.worlds)[0];
    if (!slug) return undefined;
    const catalogue = catalogueOfServed(answer.worlds);
    const entry = worldIn(catalogue, slug);
    if (!entry) return undefined;
    const steps: [Step, ...Step[]] = entry.placement
      ? [
          {
            id: 'rondkijken',
            title: BEKIJKEN_TEXT.rooms,
            task: { type: 'furnish', instruction: BEKIJKEN_TEXT.roomsText },
          },
        ]
      : [
          {
            id: LOOK_STEP,
            title: BEKIJKEN_TEXT.look,
            panel: { title: name, text: BEKIJKEN_TEXT.lookAround },
          },
        ];
    const lesson: Lesson = {
      ...frame('world', name),
      scene: { type: 'world', world: slug },
      steps,
    };
    return {
      lesson,
      world: {
        slug,
        placement: entry.placement,
        features: entry.placement ? {} : entry.features,
        initial: worldStateAt(lesson, 0, catalogue),
      },
      object: null,
      points: entry.placement || !entry.staged ? [] : worldPoints(),
    };
  }
  const slug = Object.keys(answer.models)[0];
  const facts = slug ? answer.models[slug] : undefined;
  if (!slug || !facts) return undefined;
  const place = standingPlace(facts);
  const clips = MODEL_ANIMATIONS.filter((clip) => facts.animations[clip] !== undefined);
  const steps: [Step, ...Step[]] = [
    {
      id: LOOK_STEP,
      title: BEKIJKEN_TEXT.look,
      panel: { title: name, text: BEKIJKEN_TEXT.walkAround },
    },
    ...clips.map((clip): Step => ({
      id: `anim-${clip}`,
      title: clipLabel(clip),
      panel: { title: clipLabel(clip), text: `${name}: ${clip}.` },
      models: [{ object: 'item', play: clip, loop: true }],
    })),
  ];
  const lesson: Lesson = {
    ...frame('object', name),
    scene: { type: 'studio' },
    objects: {
      item: { kind: `model:${slug}`, label: name.slice(0, 60), pos: place.pos, turn: place.turn },
    },
    steps,
  };
  return {
    lesson,
    world: null,
    object: { slug, clips, pos: place.pos, turn: place.turn, reach: place.reach },
    points: objectPoints(place),
  };
}
